// src/api/progress.ts
import { API_BASE, buildHeaders, fetchWithAuth } from "../utils/api";
import type { AccessSnapshot } from "./snapshot";
import { get as storeGet, put as storePut, compositeKey } from "../utils/store";

export type ProgressDay = {
  day_number: number;
  local_date: string;
};

// M5-STORE2 (16.09.2026): status по образцу content.ts/snapshot.ts. Раньше
// "легитимно нет прогресса" (401/403/404/400 — не авторизован/чужой курс/нет
// enrollment) и "не удалось спросить" (сеть/5xx) были неразличимы — оба
// схлопывались в ok:false. Различие нужно не для рендера (обе ветки рисуют
// календарь без отметок, без ошибки — см. DashboardScreen), а для кэша:
// легитимно пустой прогресс не кладём в кэш и не подменяем им кэш чужого
// состояния; настоящий отказ спросить — читает кэш. ok сохранён как было
// (true для "ok"/"cached" — кэш обрабатывается наравне с живым ответом,
// как решено для баланса искорок в App.tsx и для снапшота в snapshot.ts).
export type ProgressDays = {
  ok: boolean;
  status: "ok" | "empty" | "unavailable" | "cached";
  days: ProgressDay[];
  today_completed: boolean;
  today_ymd: string;
  cachedAt?: number;
  stale?: boolean;
};

const EMPTY_DAYS: ProgressDays = { ok: false, status: "empty", days: [], today_completed: false, today_ymd: "" };
const UNAVAILABLE_DAYS: ProgressDays = { ok: false, status: "unavailable", days: [], today_completed: false, today_ymd: "" };

type StoredProgress = { days: ProgressDay[]; today_completed: boolean; today_ymd: string };

/** Спросить кэш при сетевом отказе. Пусто в кэше → unavailable, как раньше. */
async function cachedOrUnavailable(key: string): Promise<ProgressDays> {
  const cached = await storeGet<StoredProgress>(key);
  if (cached.status === "empty") return UNAVAILABLE_DAYS;
  console.error(`[progress] served from cache, age=${Date.now() - cached.cachedAt}`);
  return {
    ok: true,
    status: "cached",
    days: cached.data.days,
    today_completed: cached.data.today_completed,
    today_ymd: cached.data.today_ymd,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

export async function getDays(courseId: number | string): Promise<ProgressDays> {
  const key = compositeKey("progress_days", courseId);

  let res: Response;
  try {
    res = await fetchWithAuth(
      `${API_BASE}/api/progress/days?course_id=${encodeURIComponent(String(courseId))}`,
      { headers: buildHeaders(false) }
    );
  } catch (e) {
    console.error("[progress] network error:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }

  // Легитимно "нет прогресса" (не авторизован/чужой курс/нет enrollment) —
  // не отказ спросить. В кэш не заглядываем и не пишем.
  if (res.status === 404 || res.status === 400 || res.status === 403 || res.status === 401) {
    return EMPTY_DAYS;
  }

  if (!res.ok) {
    console.error("[progress] unexpected error status:", res.status);
    return cachedOrUnavailable(key);
  }

  try {
    const data = (await res.json()) as { ok?: boolean; days?: ProgressDay[]; today_completed?: boolean; today_ymd?: string };
    const result: ProgressDays = {
      ok: true,
      status: "ok",
      days: Array.isArray(data?.days) ? data.days : [],
      today_completed: !!data?.today_completed,
      today_ymd: String(data?.today_ymd ?? ""),
    };
    await storePut(
      key,
      { days: result.days, today_completed: result.today_completed, today_ymd: result.today_ymd },
      0 // нет локального источника user_id в этом модуле — см. content.ts, тот же довод
    );
    return result;
  } catch (e) {
    console.error("[progress] 200 but json parse failed:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }
}

export type CompleteDayResult =
  | { ok: true; snapshot: AccessSnapshot }
  // M5-QUEUE (20.09.2026): status добавлен для очереди (utils/queue.ts) —
  // классифицирует отказ (терминальный / истёкшая сессия / временный) по
  // HTTP-коду ответа. Отсутствует при сетевой ошибке (fetch не долетел, нет
  // ответа сервера вовсе) — это и есть признак "сеть", отдельный от любого
  // HTTP-статуса. Прямые вызыватели (DashboardScreen, онлайн-путь) это поле
  // не читали и не читают — добавление не меняет их поведение.
  | { ok: false; error: string; status?: number };

export async function completeDay(
  courseId: number | string,
  completedAtIso: string
): Promise<CompleteDayResult> {
  try {
    const r = await fetchWithAuth(`${API_BASE}/api/progress/complete-day`, {
      method: "POST",
      headers: buildHeaders(true),
      body: JSON.stringify({ course_id: courseId, completed_at: completedAtIso }),
    });
    if (!r.ok) {
      let err = `http_${r.status}`;
      try { const j = await r.json(); if (j?.error) err = String(j.error); } catch {}
      return { ok: false, error: err, status: r.status };
    }
    const snap = await r.json();
    return { ok: true, snapshot: snap };
  } catch {
    return { ok: false, error: "network_error" };
  }
}
